/**
 * AutoMazen Related Products JavaScript
 * Handles row-based product card alignment and responsive behaviors
 */

class AutoMazenRelatedProducts {
  constructor() {
    this.sections = [];
    this.resizeTimeout = null;
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.initSections());
    } else {
      this.initSections();
    }
  }

  initSections() {
    const sections = document.querySelectorAll('.automazen-related-products');
    
    sections.forEach(section => {
      const sectionData = {
        element: section,
        id: section.dataset.sectionId,
        cardsDesktop: parseInt(section.dataset.cardsDesktop) || 4,
        cardsMobile: parseInt(section.dataset.cardsMobile) || 2,
        animations: section.dataset.animations === 'true',
        style: section.dataset.style || 'modern'
      };

      this.sections.push(sectionData);
      this.setupSection(sectionData);
    });

    // Setup resize handler
    this.setupResize();
    
    // Initial alignment
    this.alignAllSections();
  }

  setupSection(sectionData) {
    const { element } = sectionData;
    
    // Apply CSS variables from section settings
    this.applySectionSettings(element);
    
    // Setup intersection observer for animations
    if (sectionData.animations) {
      this.setupIntersectionObserver(element);
    }
    
    // Setup image loading optimization
    this.setupLazyLoading(element);
  }

  applySectionSettings(section) {
    // CSS variables are handled by Liquid template
    // JavaScript focuses on dynamic behaviors only
    
    // Apply dynamic image aspect ratios
    this.applyImageAspectRatios(section);
    
    // Apply any dynamic responsive behaviors
    this.applyDynamicBehaviors(section);
  }

  applyImageAspectRatios(section) {
    // Apply image aspect ratios dynamically
    const desktopRatio = section.dataset.cardImageRatioDesktop;
    const mobileRatio = section.dataset.cardImageRatioMobile;
    
    if (desktopRatio) {
      section.setAttribute('data-card-image-ratio-desktop', desktopRatio);
    }
    
    if (mobileRatio) {
      section.setAttribute('data-card-image-ratio-mobile', mobileRatio);
    }
  }

  applyDynamicBehaviors(section) {
    // Handle any dynamic responsive behaviors that can't be done in CSS
    // Currently focused on card alignment which is handled separately
    
    // Future: Add any dynamic behaviors needed for responsive design
  }

  setupIntersectionObserver(section) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
            this.staggerCardAnimations(entry.target);
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '50px'
      }
    );

    observer.observe(section);
  }

  staggerCardAnimations(section) {
    const cards = section.querySelectorAll('.product-card');
    
    cards.forEach((card, index) => {
      setTimeout(() => {
        card.style.opacity = '1';
        card.style.transform = 'translateY(0)';
      }, index * 100);
    });
  }

  setupLazyLoading(section) {
    const images = section.querySelectorAll('.product-img');
    
    if ('IntersectionObserver' in window) {
      const imageObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            img.classList.add('loaded');
            imageObserver.unobserve(img);
          }
        });
      });

      images.forEach(img => imageObserver.observe(img));
    }
  }

  alignAllSections() {
    this.sections.forEach(sectionData => {
      this.alignSection(sectionData);
    });
  }

  alignSection(sectionData) {
    this.alignCardDescriptions(sectionData);
    this.alignProductCards(sectionData);
  }

  /**
   * Critical alignment function based on proven Collection Cards architecture
   * Processes each .grid-row independently for perfect horizontal alignment
   */
  alignCardDescriptions(sectionData) {
    const { element } = sectionData;
    const gridRows = element.querySelectorAll('.grid-row');
    
    // Skip alignment on mobile (single card rows)
    if (window.innerWidth <= 768) {
      // Reset any previous height modifications on mobile
      gridRows.forEach(row => {
        const titles = row.querySelectorAll('.product-title');
        titles.forEach(title => {
          title.style.height = 'auto';
        });
      });
      return;
    }
    
    // Process each row independently
    gridRows.forEach(row => {
      const titles = row.querySelectorAll('.product-title');
      
      if (titles.length <= 1) return; // Skip single card rows
      
      // Reset heights first
      titles.forEach(title => {
        title.style.height = 'auto';
      });
      
      // Find the tallest title in this row
      let maxHeight = 0;
      titles.forEach(title => {
        const height = title.offsetHeight;
        if (height > maxHeight) {
          maxHeight = height;
        }
      });
      
      // Apply the max height to all titles in this row
      titles.forEach(title => {
        title.style.height = `${maxHeight}px`;
      });
    });
  }

  alignProductCards(sectionData) {
    const { element } = sectionData;
    const isMobile = window.innerWidth <= 768;
    
    // Skip alignment for single-card rows on mobile
    if (isMobile && sectionData.cardsMobile === 1) {
      this.resetCardHeights(element);
      return;
    }

    // Get the appropriate rows (desktop or mobile)
    // Liquid creates .grid-row and .grid-row.mobile-row classes
    const rows = element.querySelectorAll(isMobile ? '.grid-row.mobile-row' : '.grid-row:not(.mobile-row)');
    
    rows.forEach(row => {
      this.alignRowCards(row);
    });
  }

  alignRowCards(row) {
    const cards = row.querySelectorAll('.product-card');
    const titles = row.querySelectorAll('.product-title');
    
    // Reset previous alignments
    titles.forEach(title => {
      title.style.minHeight = '';
    });

    // Find the tallest title in this row
    let maxTitleHeight = 0;
    titles.forEach(title => {
      const height = title.offsetHeight;
      if (height > maxTitleHeight) {
        maxTitleHeight = height;
      }
    });

    // Apply the max height to all titles in this row
    titles.forEach(title => {
      title.style.minHeight = `${maxTitleHeight}px`;
    });

    // Ensure equal card heights using flexbox
    cards.forEach(card => {
      card.style.height = 'auto';
    });

    // Let flexbox handle the equal heights naturally
    requestAnimationFrame(() => {
      let maxCardHeight = 0;
      cards.forEach(card => {
        const height = card.offsetHeight;
        if (height > maxCardHeight) {
          maxCardHeight = height;
        }
      });

      cards.forEach(card => {
        card.style.minHeight = `${maxCardHeight}px`;
      });
    });
  }

  resetCardHeights(section) {
    const titles = section.querySelectorAll('.product-title');
    const cards = section.querySelectorAll('.product-card');
    
    titles.forEach(title => {
      title.style.minHeight = '';
    });
    
    cards.forEach(card => {
      card.style.minHeight = '';
      card.style.height = '';
    });
  }

  // Critical missing method that connects all alignment functionality
  alignAllSections() {
    this.sections.forEach(sectionData => {
      // Use the proven alignment approach from Collection Cards
      this.alignCardDescriptions(sectionData);
      this.alignProductCards(sectionData);
    });
  }

  setupResize() {
    window.addEventListener('resize', () => {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => {
        this.alignAllSections();
      }, 250);
    });
  }

  // Public method to refresh alignment (useful for theme customization)
  refresh() {
    setTimeout(() => {
      this.alignAllSections();
    }, 100);
  }

  // Public method to add new section dynamically
  addSection(sectionElement) {
    const sectionData = {
      element: sectionElement,
      id: sectionElement.dataset.sectionId,
      cardsDesktop: parseInt(sectionElement.dataset.cardsDesktop) || 4,
      cardsMobile: parseInt(sectionElement.dataset.cardsMobile) || 2,
      animations: sectionElement.dataset.animations === 'true',
      style: sectionElement.dataset.style || 'modern'
    };

    this.sections.push(sectionData);
    this.setupSection(sectionData);
    this.alignProductCards(sectionData);
  }
}

// Performance optimization - Initialize only when needed
const initRelatedProducts = () => {
  if (document.querySelector('.automazen-related-products')) {
    window.AutoMazenRelatedProducts = new AutoMazenRelatedProducts();
  }
};

// Initialize
initRelatedProducts();

// Expose for Shopify theme customization
if (typeof Shopify !== 'undefined') {
  window.AutoMazenRelatedProducts = AutoMazenRelatedProducts;
}
